import type { Client } from '@urql/core'

import { graphql } from 'app/generated/gql'
import type { SmsTemplate, SmsTemplateListResult } from 'app/generated/graphql'
import { toEngineText } from 'app/lib/messageTemplates'
import { importBrowserTemplates, type BrowserTemplate } from './browserTemplateImport'
import type { ListParams } from './EntityStore'
import { EntityStore } from './EntityStore'

const LIST_QUERY = graphql(`
  query SmsTemplates($search: String, $pagination: PaginationInput, $sort: SortInput) {
    smsTemplates(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        orgId
        workspaceId
        name
        body
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query SmsTemplate($id: ID!) {
    smsTemplate(id: $id) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      body
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateSmsTemplate($input: CreateSmsTemplateInput!) {
    createSmsTemplate(input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      body
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateSmsTemplate($id: ID!, $input: UpdateSmsTemplateInput!) {
    updateSmsTemplate(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      body
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteSmsTemplate($id: ID!) {
    deleteSmsTemplate(id: $id)
  }
`)

/** The whole workspace's templates in one page, newest edit first. */
const SMS_TEMPLATE_LIST_PARAMS: ListParams = {
  pagination: { page: 0, limit: 200 },
  sort: { field: `updatedDate`, direction: `DESC` },
}

export type SmsTemplateInput = { name: string; body: string }

type BrowserSmsTemplate = BrowserTemplate & SmsTemplateInput

export class SmsTemplateStore extends EntityStore<
  SmsTemplate,
  SmsTemplateListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `SmsTemplate`,
      extractList: (data: any) => data.smsTemplates,
      extractOne: (data: any) => data.smsTemplate,
      extractMutated: (data: any) => data.updateSmsTemplate ?? data.createSmsTemplate,
    })
  }

  /** The open workspace's templates. The list can still hold the previous workspace's rows
   *  for a moment after a switch, until the next fetch lands. */
  forWorkspace(workspaceId: string | undefined): SmsTemplate[] {
    return workspaceId ? this.hits.filter((t) => t.workspaceId === workspaceId) : []
  }

  /** Network-only: the server is shared with every user of the workspace, and a cached list
   *  could belong to the workspace open before a switch. */
  load() {
    return this.fetchList(SMS_TEMPLATE_LIST_PARAMS, { requestPolicy: `network-only` })
  }

  importBrowserTemplates(workspaceId: string) {
    return importBrowserTemplates<BrowserSmsTemplate, SmsTemplateInput>({
      storageKey: `trk:smsTemplates`,
      workspaceId,
      toInput: (t) => ({ name: t.name, body: toEngineText(t.body, t.fallbacks) }),
      fetchServer: async () => {
        const result = await this.load()
        const rows = this.forWorkspace(workspaceId).map((t) => ({ name: t.name ?? ``, body: t.body ?? `` }))
        return { rows, failed: !!result.error }
      },
      create: (input) => this.create(input),
      sameTemplate: (a, b) => a.name.trim() === b.name.trim() && a.body === b.body,
    })
  }
}
